// Shared utility for managing learning paths data with localStorage and JSON fallback

const LEARNING_PATHS_KEY = 'bootminds_learning_paths';
const DEFAULT_JSON_PATH = '/data/learningPathsData.json';

// Load learning paths from localStorage or fallback to JSON file
export const loadLearningPaths = async () => {
  console.log('=== LearningPathsStorage: loadLearningPaths called ===');
  try {
    // First try localStorage
    const stored = localStorage.getItem(LEARNING_PATHS_KEY);
    console.log('LearningPathsStorage: Checking localStorage for key:', LEARNING_PATHS_KEY);
    if (stored) {
      console.log('LearningPathsStorage: Found data in localStorage, parsing...');
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        console.log('LearningPathsStorage: Loaded from localStorage:', parsed.length, 'paths');
        return parsed;
      } else {
        console.log('LearningPathsStorage: localStorage data is empty or invalid');
      }
    }

    // Fallback to JSON file
    console.log('LearningPathsStorage: Loading from JSON fallback');
    console.log('LearningPathsStorage: Fetching from path:', DEFAULT_JSON_PATH);
    const response = await fetch(DEFAULT_JSON_PATH);
    if (!response.ok) {
      throw new Error(`Failed to fetch JSON: ${response.status}`);
    }
    const data = await response.json();
    console.log('LearningPathsStorage: Loaded from JSON:', data.length, 'paths');

    // Save to localStorage for future use
    localStorage.setItem(LEARNING_PATHS_KEY, JSON.stringify(data));
    console.log('LearningPathsStorage: Saved JSON data to localStorage');
    return data;

  } catch (error) {
    console.error('LearningPathsStorage: Error loading data:', error);
    console.error('LearningPathsStorage: Error stack:', error.stack);
    // Return empty array as final fallback
    return [];
  }
};

// Save learning paths to localStorage
export const saveLearningPaths = (learningPaths) => {
  console.log('=== LearningPathsStorage: saveLearningPaths called ===');
  try {
    console.log('LearningPathsStorage: Saving', learningPaths.length, 'paths to localStorage');
    localStorage.setItem(LEARNING_PATHS_KEY, JSON.stringify(learningPaths));
    console.log('LearningPathsStorage: Saved to localStorage:', learningPaths.length, 'paths');

    // Dispatch custom event for same-tab updates
    console.log('LearningPathsStorage: Dispatching storage change event');
    window.dispatchEvent(new CustomEvent('learningPathsStorageChanged'));
  } catch (error) {
    console.error('LearningPathsStorage: Error saving to localStorage:', error);
    console.error('LearningPathsStorage: Error details:', error.stack);
  }
};

// Add a new learning path
export const addLearningPath = (newPath) => {
  console.log('=== LearningPathsStorage: addLearningPath called ===');
  console.log('LearningPathsStorage: Adding new path with title:', newPath.title);
  const currentPaths = JSON.parse(localStorage.getItem(LEARNING_PATHS_KEY) || '[]');
  console.log('LearningPathsStorage: Current paths count:', currentPaths.length);
  const updatedPaths = [...currentPaths, {
    ...newPath,
    id: `lp-${Date.now()}`, // Generate unique ID
    created_at: new Date().toISOString(),
    status: newPath.status || 'draft'
  }];
  console.log('LearningPathsStorage: New paths count:', updatedPaths.length);
  saveLearningPaths(updatedPaths);
  return updatedPaths;
};

// Update an existing learning path
export const updateLearningPath = (updatedPath) => {
  const currentPaths = JSON.parse(localStorage.getItem(LEARNING_PATHS_KEY) || '[]');
  const updatedPaths = currentPaths.map(path =>
    path.id === updatedPath.id ? { ...updatedPath, updated_at: new Date().toISOString() } : path
  );
  saveLearningPaths(updatedPaths);
  return updatedPaths;
};

// Delete a learning path
export const deleteLearningPath = (pathId) => {
  const currentPaths = JSON.parse(localStorage.getItem(LEARNING_PATHS_KEY) || '[]');
  const updatedPaths = currentPaths.filter(path => path.id !== pathId);
  saveLearningPaths(updatedPaths);
  return updatedPaths;
};

// Publish a learning path
export const publishLearningPath = (pathId) => {
  const currentPaths = JSON.parse(localStorage.getItem(LEARNING_PATHS_KEY) || '[]');
  const updatedPaths = currentPaths.map(path =>
    path.id === pathId ? {
      ...path,
      status: 'published',
      published_at: new Date().toISOString()
    } : path
  );
  saveLearningPaths(updatedPaths);
  return updatedPaths;
};

// Reset to JSON defaults (useful for admin)
export const resetToDefaults = async () => {
  console.log('=== LearningPathsStorage: resetToDefaults called ===');
  try {
    console.log('LearningPathsStorage: Fetching JSON defaults from:', DEFAULT_JSON_PATH);
    const response = await fetch(DEFAULT_JSON_PATH);
    if (!response.ok) {
      throw new Error(`Failed to fetch JSON: ${response.status}`);
    }
    const data = await response.json();
    console.log('LearningPathsStorage: Loaded', data.length, 'default paths');
    saveLearningPaths(data);
    console.log('LearningPathsStorage: Reset to JSON defaults completed');
    return data;
  } catch (error) {
    console.error('LearningPathsStorage: Error resetting to defaults:', error);
    console.error('LearningPathsStorage: Error stack:', error.stack);
    return [];
  }
};

// Export to JSON file (for backup/sharing)
export const exportToJson = () => {
  const data = localStorage.getItem(LEARNING_PATHS_KEY);
  if (!data) return null;

  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `learning-paths-backup-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Import from JSON file
export const importFromJson = (jsonData) => {
  try {
    const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
    if (Array.isArray(parsed)) {
      saveLearningPaths(parsed);
      console.log('LearningPathsStorage: Imported from JSON:', parsed.length, 'paths');
      return parsed;
    } else {
      throw new Error('Invalid JSON format');
    }
  } catch (error) {
    console.error('LearningPathsStorage: Error importing from JSON:', error);
    throw error;
  }
};
